/**
 * Hosts the subscribe dialog over the working app (TMMAPP-0116), mounted by `AppShell`:
 *
 * - `openSubscribe()` opens it in `offer` mode for a guest, `dismissible` for everyone else;
 * - an invitation just accepted (`SUBSCRIBE_OFFER_STATE`) opens `offer` mode on arrival, before
 *   the guest tour; the state is consumed so Back or a reload does not reopen it;
 * - a lapsed subscriber in grace gets the dismissible dialog once per session (not on every route
 *   change); Renew opens it again;
 * - in grace with a re-subscribe in progress (`resume`, TMMAPP-0117 B19), it opens on every arrival
 *   and resumes from `/me/billing.pending` (the dialog's own read), whatever this session showed.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMe } from '@/session/meHooks';
import { graceShown, markGraceShown } from './graceShown';
import { SubscribeDialog, type SubscribeDialogMode } from './SubscribeDialog';
import {
  hasSubscribeOfferState,
  SubscribeGateContext,
  SubscribeOfferContext,
  type SubscribeOfferValue,
} from './subscribeOfferContext';

export function SubscribeOfferProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const gate = useContext(SubscribeGateContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<SubscribeDialogMode | null>(null);
  // Grace: open when the gate settles (on arrival, or after an owner's `/me/billing` read answers)
  // unless this session has shown it already; a parked re-subscribe always. State adjusted while
  // rendering, the derived-state pattern.
  const [seenGate, setSeenGate] = useState<typeof gate | null>(null);
  if (seenGate !== gate) {
    setSeenGate(gate);
    if (gate === 'resume' || (gate === 'grace' && !graceShown())) setMode('dismissible');
  }
  const tierMode: SubscribeDialogMode = me.tier === 'GUEST' ? 'offer' : 'dismissible';
  const invited = hasSubscribeOfferState(location.state) && me.tier !== 'SUBSCRIBER';
  const [offeredFor, setOfferedFor] = useState<string | null>(null);
  const consumed = useRef<string | null>(null);

  const openSubscribe = useCallback(() => setMode(tierMode), [tierMode]);

  useEffect(() => {
    if (gate === 'grace' || gate === 'resume') markGraceShown();
  }, [gate]);

  // An invitation just accepted: offer mode before the tour (state adjusted while rendering, the
  // derived-state pattern), then the state is dropped from history so Back or a reload doesn't
  // reopen it.
  if (invited && offeredFor !== location.key) {
    setOfferedFor(location.key);
    setMode('offer');
  }
  useEffect(() => {
    if (!invited || consumed.current === location.key) return;
    consumed.current = location.key;
    void navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [invited, location.key, location.pathname, location.search, location.hash, navigate]);

  const close = useCallback(() => setMode(null), []);
  // An invitation's state still in the location counts as open, so the tour never starts under it.
  const dialogOpen = mode !== null || invited;
  const value = useMemo<SubscribeOfferValue>(
    () => ({ openSubscribe, dialogOpen }),
    [openSubscribe, dialogOpen],
  );

  return (
    <SubscribeOfferContext.Provider value={value}>
      {children}
      {/* Mounted only while open: a closed dialog costs the shell nothing on every render. */}
      {mode !== null && <SubscribeDialog open mode={mode} onClose={close} />}
    </SubscribeOfferContext.Provider>
  );
}
